import React, { ReactNode, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/toaster";
import { useIsMobile } from "@/hooks/use-mobile";
import MobileMenu from "./MobileMenu";
import { ModeToggle } from "@/components/theme/ModeToggle";
import {
  Home,
  MessageSquare,
  Calendar,
  Users,
  CalendarClock,
  User,
  LogOut,
  Loader,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface DashboardLayoutProps {
  children: ReactNode;
  title?: string;
  isLoading?: boolean;
}

const DashboardLayout = ({ children, title, isLoading = false }: DashboardLayoutProps) => {
  const { user, signOut, profile, isAdmin } = useAuth();
  const location = useLocation();
  const isMobile = useIsMobile();

  const isPathActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="flex h-screen overflow-hidden">
        {/* Sidebar for desktop */}
        <div className="hidden md:flex md:flex-shrink-0">
          <div className="flex flex-col w-64">
            <div className="flex flex-col h-0 flex-1 bg-lawyer-dark dark:bg-gray-950">
              <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
                <div className="flex items-center justify-between flex-shrink-0 px-4">
                  <Link to="/" className="flex items-center">
                    <span className="text-white font-display font-bold text-2xl">
                      Lex<span className="text-lawyer-secondary">Prompt</span>
                    </span>
                  </Link>
                  <ModeToggle variant="white" />
                </div>
                <nav className="mt-8 flex-1 px-2 space-y-1">
                  {/* Dashboard */}
                  <Link
                    to="/dashboard"
                    className={cn(
                      "group flex items-center px-2 py-2 text-sm font-medium rounded-md",
                      isPathActive('/dashboard') && !isPathActive('/dashboard/')
                        ? "bg-lawyer-primary text-white dark:bg-lawyer-secondary dark:text-lawyer-dark"
                        : "text-gray-300 hover:bg-lawyer-primary/20 hover:text-white dark:text-gray-300 dark:hover:bg-lawyer-secondary/20 dark:hover:text-white"
                    )}
                  >
                    <Home className="mr-3 flex-shrink-0 h-6 w-6" />
                    Dashboard
                  </Link>

                  {/* Prompts */}
                  <Link
                    to="/dashboard/prompts"
                    className={cn(
                      "group flex items-center px-2 py-2 text-sm font-medium rounded-md",
                      isPathActive('/dashboard/prompts')
                        ? "bg-lawyer-primary text-white dark:bg-lawyer-secondary dark:text-lawyer-dark"
                        : "text-gray-300 hover:bg-lawyer-primary/20 hover:text-white dark:text-gray-300 dark:hover:bg-lawyer-secondary/20 dark:hover:text-white"
                    )}
                  >
                    <MessageSquare className="mr-3 flex-shrink-0 h-6 w-6" />
                    Prompts
                  </Link>

                  {/* Agenda Section */}
                  <div className="pt-4">
                    <h3 className="px-2 text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Agenda
                    </h3>
                    <div className="mt-2 space-y-1">
                      <Link
                        to="/dashboard/agenda"
                        className={cn(
                          "group flex items-center px-2 py-2 text-sm font-medium rounded-md",
                          isPathActive('/dashboard/agenda')
                            ? "bg-lawyer-primary text-white dark:bg-lawyer-secondary dark:text-lawyer-dark"
                            : "text-gray-300 hover:bg-lawyer-primary/20 hover:text-white dark:text-gray-300 dark:hover:bg-lawyer-secondary/20 dark:hover:text-white"
                        )}
                      >
                        <Calendar className="mr-3 flex-shrink-0 h-6 w-6" />
                        Calendário
                      </Link>

                      <Link
                        to="/dashboard/clients"
                        className={cn(
                          "group flex items-center px-2 py-2 text-sm font-medium rounded-md",
                          isPathActive('/dashboard/clients')
                            ? "bg-lawyer-primary text-white dark:bg-lawyer-secondary dark:text-lawyer-dark"
                            : "text-gray-300 hover:bg-lawyer-primary/20 hover:text-white dark:text-gray-300 dark:hover:bg-lawyer-secondary/20 dark:hover:text-white"
                        )}
                      >
                        <Users className="mr-3 flex-shrink-0 h-6 w-6" />
                        Clientes
                      </Link>

                      <Link
                        to="/dashboard/appointments"
                        className={cn(
                          "group flex items-center px-2 py-2 text-sm font-medium rounded-md",
                          isPathActive('/dashboard/appointments')
                            ? "bg-lawyer-primary text-white dark:bg-lawyer-secondary dark:text-lawyer-dark"
                            : "text-gray-300 hover:bg-lawyer-primary/20 hover:text-white dark:text-gray-300 dark:hover:bg-lawyer-secondary/20 dark:hover:text-white"
                        )}
                      >
                        <CalendarClock className="mr-3 flex-shrink-0 h-6 w-6" />
                        Compromissos
                      </Link>
                    </div>
                  </div>

                  {/* Profile */}
                  <Link
                    to="/dashboard/profile"
                    className={cn(
                      "group flex items-center px-2 py-2 text-sm font-medium rounded-md",
                      isPathActive('/dashboard/profile')
                        ? "bg-lawyer-primary text-white dark:bg-lawyer-secondary dark:text-lawyer-dark"
                        : "text-gray-300 hover:bg-lawyer-primary/20 hover:text-white dark:text-gray-300 dark:hover:bg-lawyer-secondary/20 dark:hover:text-white"
                    )}
                  >
                    <User className="mr-3 flex-shrink-0 h-6 w-6" />
                    Perfil
                  </Link>
                </nav>
              </div>
              <div className="flex-shrink-0 flex border-t border-gray-700 p-4">
                <div className="flex-shrink-0 w-full group block">
                  <div className="flex items-center">
                    <div className="ml-3">
                      <p className="text-sm font-medium text-white">
                        {profile?.full_name || user?.email}
                      </p>
                      <button
                        onClick={() => signOut()}
                        className="text-xs font-medium text-gray-300 group-hover:text-gray-200 flex items-center mt-1"
                      >
                        <LogOut className="mr-1 h-4 w-4" /> Sair
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="flex flex-col w-0 flex-1 overflow-hidden">
          <div className="md:hidden p-4 flex items-center justify-between bg-white border-b border-gray-200 dark:bg-gray-800 dark:border-gray-700">
            <Link to="/" className="flex items-center">
              <span className="text-lawyer-primary font-display font-bold text-xl dark:text-white">
                Lex<span className="text-lawyer-secondary">Prompt</span>
              </span>
            </Link>
            <div className="flex items-center space-x-2">
              <ModeToggle />
              <MobileMenu />
            </div>
          </div>

          <main className="flex-1 relative z-0 overflow-y-auto focus:outline-none dark:bg-gray-900">
            <div className="py-6">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
                {title && (
                  <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
                    {title}
                  </h1>
                )}
              </div>
              <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-4">
                {isLoading ? (
                  <div className="flex flex-col items-center justify-center h-64">
                    <Loader className="h-8 w-8 animate-spin text-lawyer-primary mb-4 dark:text-lawyer-secondary" />
                    <p className="text-gray-500 dark:text-gray-400">Carregando...</p>
                  </div>
                ) : (
                  children
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
      <Toaster />
    </div>
  );
};

export default DashboardLayout;